import { Box, Tooltip } from '@mantine/core';
import { MealPlan, MealPlanAssignment, Recipe, getAssignmentMealType } from '../../types/mealPlan';
import { RecipeImage } from '../UI/RecipeImage';
import { getAssignmentRecipe } from '../../utils/mealPlanHelpers';
import { pencilInImageFor } from '../../utils/pencilInImages';

// What a plan-row strip shows: dinners only. A plan with breakfast/lunch
// carries rotation repeats and reference rows that drown a one-row strip
// (twenty-plus thumbnails behind a "+15"); the dinners tell the week's story
// and the card's meta line says the other meals exist. Also tolerates failed
// plans, which can carry no assignments at all.
export const stripAssignments = (plan: MealPlan): MealPlanAssignment[] =>
  (plan.assignments ?? []).filter((a) => getAssignmentMealType(a) === 'dinner');

// True when the assignment can render a row thumbnail: a resolvable recipe,
// or a penciled-in meal (which carries its own house image)
export const hasAssignmentThumbnail = (
  assignment: MealPlanAssignment,
  plan: MealPlan
): boolean =>
  assignment.source === 'custom_meal'
    ? !!assignment.customMeal
    : !!getAssignmentRecipe(assignment, plan);

// Display name for overflow ("+N more") tooltips, covering penciled-in meals
export const assignmentThumbnailLabel = (
  assignment: MealPlanAssignment,
  plan: MealPlan
): string | undefined =>
  assignment.source === 'custom_meal'
    ? assignment.customMeal?.name
    : getAssignmentRecipe(assignment, plan)?.name;

type AssignmentThumbnailProps = {
  assignment: MealPlanAssignment;
  plan: MealPlan;
  radius: 'sm' | 'md';
  onRecipeClick: (recipe: Recipe) => void;
};

// One 80px thumbnail in a plan-row strip (dashboard upcoming plans and meal
// history). Recipe days show the dish photo, prep days dimmed under the amber
// wash. Penciled-in days show their house image under the same ghost
// treatment as the plan grid, and don't navigate — there's no recipe page
// behind them.
export const AssignmentThumbnail = ({
  assignment,
  plan,
  radius,
  onRecipeClick,
}: AssignmentThumbnailProps) => {
  const borderRadius = `var(--mantine-radius-${radius})`;

  if (assignment.source === 'custom_meal') {
    const meal = assignment.customMeal;
    if (!meal) return null;
    const { src, focusY } = pencilInImageFor(meal.name, getAssignmentMealType(assignment));
    return (
      <Tooltip
        label={meal.name}
        position="top"
        withArrow
        events={{ hover: true, focus: true, touch: true }}
      >
        <Box style={{ position: 'relative', overflow: 'hidden', borderRadius }}>
          <RecipeImage
            src={src}
            alt={meal.name}
            w={80}
            h={80}
            radius={radius}
            fallbackIconSize={32}
            style={{
              objectPosition: `center ${focusY}%`,
              filter: 'saturate(0.7)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.32)',
              pointerEvents: 'none',
              borderRadius,
            }}
          />
        </Box>
      </Tooltip>
    );
  }

  const recipe = getAssignmentRecipe(assignment, plan);
  if (!recipe) return null;
  const isPrepDay = assignment.source === 'assignment_reference';
  return (
    <Tooltip
      label={recipe.name}
      position="top"
      withArrow
      events={{ hover: true, focus: true, touch: true }}
    >
      <Box
        style={{ cursor: 'pointer', position: 'relative', overflow: 'hidden', borderRadius }}
        onClick={() => onRecipeClick(recipe)}
      >
        <RecipeImage
          src={recipe.imageUrl}
          alt={recipe.name}
          w={80}
          h={80}
          radius={radius}
          fallbackIconSize={32}
          style={
            isPrepDay
              ? {
                  filter: 'saturate(0.4) brightness(0.9)',
                  opacity: 0.5,
                }
              : undefined
          }
        />
        {isPrepDay && recipe.imageUrl && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                'linear-gradient(135deg, rgba(251, 146, 60, 0.12) 0%, rgba(245, 158, 11, 0.15) 100%)',
              pointerEvents: 'none',
              borderRadius,
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
};
